/**
 * LiveClassAnalyticsPage.jsx — Live Class Analytics Dashboard.
 *
 * Sprint LMS-Live · Part F.  Aggregates KPIs across every live session
 * visible to the current trainer/admin.  Uses navy/gold premium theme.
 *
 * Scope:
 *   super_admin             → all sessions
 *   org_admin / event_manager → all sessions in their organisation
 *   chair / faculty_advisor → only sessions they own / co-host
 *
 * Provider names are NEVER shown — only the public "Diplomexa Live"
 * label.  Diploks are referenced only when surfacing the premium-AI
 * usage strip; Podium Points are never shown on this admin surface.
 */
import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import {
  getLiveAnalyticsDashboard, formatApiError, liveAnalyticsCsvUrl,
} from "../../lib/liveAnalyticsApi";
import { toast } from "sonner";
import {
  Activity, Users, Clock4, Sparkles, Video, Star, Download,
  ChevronRight, Hourglass, ArrowUpRight,
} from "lucide-react";

const LOOKBACKS = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 365, label: "Last year" },
];

export default function LiveClassAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lookbackDays, setLookbackDays] = useState(30);

  const load = async (days) => {
    setLoading(true);
    try {
      const d = await getLiveAnalyticsDashboard({ lookbackDays: days });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(lookbackDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookbackDays]);

  const totals = data?.totals || {};
  const recordings = data?.recordings || {};
  const premium = data?.premium_ai_usage || {};
  const trainers = data?.trainer_breakdown || [];
  const topSessions = data?.top_sessions || [];

  return (
    <DashboardLayout title="Live Class Analytics">
      <div data-testid="live-analytics-page" className="space-y-6 pb-12">
        <Hero
          lookbackDays={lookbackDays}
          onChange={setLookbackDays}
          scope={data?.scope}
        />

        {loading || !data ? (
          <div className="grid h-72 place-items-center text-sm text-slate-500">
            Loading analytics…
          </div>
        ) : (
          <>
            <KpiRow totals={totals} />

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
              <main className="lg:col-span-8 space-y-6">
                <TopSessions rows={topSessions} />
                <Timeline rows={data.timeline || []} />
                {trainers.length > 0 ? (
                  <TrainerBreakdown rows={trainers} />
                ) : null}
              </main>

              <aside className="lg:col-span-4 space-y-6">
                <RecordingsCard rows={recordings} />
                <PremiumUsageCard rows={premium} />
                <PolicyCard />
              </aside>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

// =============================================================================
// Sub-components
// =============================================================================
function Hero({ lookbackDays, onChange, scope }) {
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const csvHref = `${apiBase}/api${liveAnalyticsCsvUrl({ lookbackDays })}`;
  return (
    <section
      data-testid="live-analytics-hero"
      className="relative overflow-hidden rounded-3xl border border-[#0F4C81] bg-gradient-to-br from-[#0A192F] via-[#0F4C81] to-[#0A192F] p-6 md:p-7 shadow-2xl text-white"
    >
      <div className="absolute -top-32 -right-24 h-72 w-72 rounded-full bg-[#D4AF37]/15 blur-3xl" />
      <div className="absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-[#0F4C81]/40 blur-3xl" />
      <div className="relative flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-bold flex items-center gap-2">
            <Activity size={12} /> Diplomexa Live · Analytics
          </p>
          <h1 className="mt-1 text-3xl md:text-4xl font-bold leading-tight">
            Live Class Analytics
          </h1>
          <p className="mt-2 max-w-xl text-sm text-blue-100">
            Attendance, engagement, recordings &amp; premium AI usage —
            for every Diplomexa Live session you can see.
          </p>
          {scope ? (
            <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#D4AF37]">
              Scope · {scope.replace("_", " ")}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            data-testid="live-analytics-lookback"
            className="flex items-center rounded-full bg-white/10 p-1"
          >
            {LOOKBACKS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange(opt.value)}
                data-testid={`live-analytics-lookback-${opt.value}`}
                className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] transition ${
                  lookbackDays === opt.value
                    ? "bg-[#D4AF37] text-[#0A192F]"
                    : "text-white/90 hover:text-white"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <a
            data-testid="live-analytics-csv-export"
            href={csvHref}
            className="inline-flex items-center gap-1 rounded-lg border border-[#D4AF37]/40 bg-white/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white hover:bg-white/20"
          >
            <Download size={11} /> Export CSV
          </a>
        </div>
      </div>
    </section>
  );
}

function KpiRow({ totals }) {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <Kpi
        icon={Video}
        testid="live-kpi-sessions"
        label="Sessions"
        value={totals.sessions || 0}
      />
      <Kpi
        icon={Users}
        testid="live-kpi-participants"
        label="Unique participants"
        value={(totals.unique_participants || 0).toLocaleString()}
      />
      <Kpi
        icon={Clock4}
        testid="live-kpi-minutes"
        label="Minutes streamed"
        value={(totals.minutes_streamed || 0).toLocaleString()}
      />
      <Kpi
        icon={Star}
        testid="live-kpi-attendance"
        label="Avg attendance"
        value={`${totals.avg_attendance_pct || 0}%`}
      />
    </div>
  );
}

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
        <Icon size={12} className="text-[#0F4C81]" /> {label}
      </div>
      <div className="mt-1 text-2xl font-bold text-[#0A192F] tabular-nums">{value}</div>
    </div>
  );
}

function TopSessions({ rows }) {
  return (
    <section
      data-testid="live-top-sessions"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
        <ArrowUpRight size={14} className="text-[#D4AF37]" /> Top Sessions
      </h2>
      {!rows.length ? (
        <p className="mt-3 text-xs text-slate-500">
          No sessions found in the selected window.
        </p>
      ) : (
        <ol className="mt-3 space-y-1.5">
          {rows.map((r, i) => (
            <li
              key={r.session_id}
              data-testid={`live-top-session-${i}`}
              className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2"
            >
              <span className="grid h-7 w-7 place-items-center rounded-full bg-[#0A192F] text-[11px] font-bold text-[#D4AF37]">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-[#0A192F] truncate">
                  {r.title || "Untitled session"}
                </p>
                <p className="text-[10px] text-slate-500">
                  {(r.start_time || "").slice(0, 16).replace("T", " ")} · status {r.status || "—"}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
                  {r.participants} attendees
                </p>
                <p className="text-xs font-bold text-[#0F4C81] tabular-nums">
                  {r.minutes_streamed} min
                </p>
              </div>
              <ChevronRight size={14} className="text-slate-300" />
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function Timeline({ rows }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <section
      data-testid="live-analytics-timeline"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
        <Hourglass size={14} className="text-[#0F4C81]" /> Sessions over time
      </h2>
      {!rows.length ? (
        <p className="mt-3 text-xs text-slate-500">
          No timeline data in this window.
        </p>
      ) : (
        <div className="mt-4 flex items-end gap-1 h-32">
          {rows.map((r) => (
            <div
              key={r.date}
              data-testid={`live-timeline-${r.date}`}
              title={`${r.date} · ${r.count} sessions`}
              className="flex-1 rounded-t bg-gradient-to-t from-[#0F4C81] to-[#D4AF37] min-w-[4px]"
              style={{ height: `${(r.count / max) * 100}%` }}
            />
          ))}
        </div>
      )}
      {rows.length > 1 ? (
        <div className="mt-2 flex justify-between text-[10px] text-slate-400">
          <span>{rows[0].date}</span>
          <span>{rows[rows.length - 1].date}</span>
        </div>
      ) : null}
    </section>
  );
}

function TrainerBreakdown({ rows }) {
  return (
    <section
      data-testid="live-trainer-breakdown"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <h2 className="text-sm font-bold uppercase tracking-[0.22em] text-[#0A192F] flex items-center gap-2">
        <Users size={14} className="text-[#D4AF37]" /> Top Trainers
      </h2>
      <ol className="mt-3 space-y-1.5">
        {rows.map((r, i) => (
          <li
            key={r.trainer_id}
            data-testid={`live-trainer-${i}`}
            className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2"
          >
            <span className="grid h-7 w-7 place-items-center rounded-full bg-[#0F4C81] text-[11px] font-bold text-white">
              {i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-[#0A192F] truncate">
                {r.trainer_name || r.trainer_id}
              </p>
              <p className="text-[10px] text-slate-500">{r.role}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
                {r.sessions} sessions · {r.unique_participants} learners
              </p>
              <p className="text-xs font-bold text-[#0F4C81] tabular-nums">
                {r.minutes_streamed} min
              </p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function RecordingsCard({ rows }) {
  return (
    <section
      data-testid="live-recordings-card"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <h3 className="text-[11px] uppercase tracking-[0.22em] text-[#0A192F] font-bold flex items-center gap-1">
        <Video size={11} className="text-[#D4AF37]" /> Recordings
      </h3>
      <div className="mt-3 space-y-1.5">
        {Object.keys(rows).length === 0 ? (
          <p className="text-xs text-slate-500">No recordings yet.</p>
        ) : (
          Object.entries(rows).map(([status, count]) => (
            <div
              key={status}
              data-testid={`live-rec-status-${status}`}
              className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-1.5 text-xs"
            >
              <span className="font-semibold text-[#0A192F] capitalize">
                {status.replace("_", " ")}
              </span>
              <span className="font-bold text-[#0F4C81] tabular-nums">
                {count}
              </span>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

function PremiumUsageCard({ rows }) {
  const entries = Object.entries(rows);
  return (
    <section
      data-testid="live-premium-usage-card"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <h3 className="text-[11px] uppercase tracking-[0.22em] text-[#0A192F] font-bold flex items-center gap-1">
        <Sparkles size={11} className="text-[#D4AF37]" /> Premium AI Usage
      </h3>
      <p className="mt-1 text-[10px] text-slate-500">
        Diploks-charged AI features powered by Diplomexa AI.
      </p>
      <div className="mt-3 space-y-1.5">
        {entries.length === 0 ? (
          <p className="text-xs text-slate-500">No premium AI runs yet.</p>
        ) : (
          entries.map(([feature, count]) => (
            <div
              key={feature}
              data-testid={`live-premium-${feature}`}
              className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-1.5 text-xs"
            >
              <span className="font-semibold text-[#0A192F]">
                {feature.replace(/_/g, " ")}
              </span>
              <span className="font-bold text-[#0F4C81] tabular-nums">
                {count}
              </span>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

function PolicyCard() {
  return (
    <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
      <p className="text-[11px] text-slate-600 leading-relaxed">
        Diplomexa Live runs on a provider-agnostic engine.{" "}
        <span className="font-semibold text-[#0A192F]">Provider names are never shown</span>{" "}
        to learners or parents. Premium AI surfaces are charged in Diploks
        and identified as <em>Diplomexa AI</em>.
      </p>
    </section>
  );
}
